// Self-contained client for affiliate-ext-service (the merchant-processing
// track). Mirrors documentsApi.ts / hardwareApi.ts — SAME shared `platform`
// cluster, different base path (/affiliate-ext).
//
// NOTE the host shape: addressed by BASE PATH, not a per-service subdomain.
// Do not "correct" this to merchant.api.<apex> — that pattern is closed (AWS
// 120-custom-domain cap).
//
// Two rules this file exists to protect:
//
//  1. A STORAGE KEY NEVER CROSSES THIS SEAM. The service strips them and
//     returns `hasFile` instead; files are fetched through a short-lived
//     presigned URL minted per click, and every mint of a sensitive document
//     is written to the record's access log first. Nothing here may cache one.
//  2. DISPATCH READINESS IS COUNTED FROM TIDs, never from `status`. A store
//     that brought its own processor never reaches `approved` and still ships.

import type {
  MerchantApplicationStatus, MerchantDocument, MerchantMessage, MerchantMessageRole,
  MerchantProfile, MerchantQuote, MerchantRateLine, TerminalAssignment, VarSheet,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveMerchantHost(
  hostname: string,
  stage: 'prod' | 'staging' | 'dev',
  envBase?: string,
): string {
  if (envBase) return envBase;
  // Dev server proxies /merchant-api → platform.api.<apiDevBase>/affiliate-ext.
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/merchant-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/affiliate-ext`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/affiliate-ext`;
  return `https://platform.api.dev.${apex}/affiliate-ext`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_MERCHANT_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveMerchantHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    // Surface the service's own message — "does not write MCC 5993",
    // "Approve with the VAR sheet" and friends are written for this reader.
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON body */ }
    throw new Error(detail || `merchant ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

const q = (params: Record<string, string | number | undefined>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/* ── shapes as the service returns them ─────────────────────────────────── */

/** A document/quote/VAR as it crosses the seam: no storage key, a `hasFile` flag. */
export type Filed<T> = Omit<T, 'storageKey'> & { hasFile: boolean };

/** One ledger row, as stored. Every field here is a RECEIPT, not a recompute. */
export interface CommissionEventRow {
  eventId: string;
  partnerId: string;
  occurredAt: number;
  sourceType: 'device_sale' | 'package_addon' | 'subscription_payment';
  sourceLabel?: string;
  grossCents: number;
  planId: string;
  planVersion: number;
  ruleId: string;
  basisApplied: string;
  rateApplied: number;
  commissionCents: number;
  status: 'pending' | 'clearing' | 'cleared' | 'paid' | 'forfeited';
  clearingDate?: number;
  orderId?: string;
}

/**
 * `reason` is the whole point of this shape: it distinguishes "nobody earned
 * anything" from "the commission engine is broken", which looked identical
 * when the order screen simply showed nothing.
 */
export interface OrderCommissionView {
  orderId: string;
  tenantId?: string;
  dealCode?: string;
  attributedPartner?: { id: string; name?: string; referralCode?: string };
  events: CommissionEventRow[];
  totalCents: number;
  unusedPartnerLinks: Array<{
    code: string; partnerId?: string; partnerName?: string; commissionPlanId?: string;
  }>;
  wouldApplyPlan?: {
    planId: string; name?: string; version: number; status?: string;
    deviceRate?: { basis: string; rate: number };
    softwareRate?: { basis: string; rate: number };
    /** Read off the deal VERSION, never the live row. */
    fromDealVersionNo?: number;
  };
  /**
   * Which version of the deal the terms above came from. `commissionPlanId` is
   * a versioned field, so the live deal row is NOT the published truth.
   */
  dealVersion?: {
    versionNo?: number;
    effectiveAt?: string;
    source: 'pinned' | 'active' | 'unversioned';
  };
  reason: 'earned' | 'no_partner_attributed' | 'partner_attributed_no_events' | 'order_not_found';
}

export interface MerchantApplicationView {
  tenantId: string;
  applicationId: string;
  locationId: string;
  locationName?: string;
  status: MerchantApplicationStatus;
  merchantSource?: 'existing' | 'new';
  assignedIsoId?: string;
  assignedIsoName?: string;
  assignedAt?: number;
  assignedBy?: string;
  profile?: MerchantProfile;
  documents: Filed<MerchantDocument>[];
  varSheets: Filed<VarSheet>[];
  quoteDocuments: Filed<MerchantQuote>[];
  terminalAssignments: TerminalAssignment[];
  declinedReason?: string;
  submittedAt?: number;
  approvedAt?: number;
  declinedAt?: number;
  createdAt: number;
  updatedAt?: number;
  thread: MerchantMessage[];
  summary: { headline: string; waitingOn: 'tenant' | 'iso' | 'processor' | 'rubytill' | 'nobody' };
  activeQuote?: Filed<MerchantQuote>;
  outstandingDocuments: Filed<MerchantDocument>[];
  accessLog?: { at: number; by: string; actor: string; action: string; documentId?: string }[];
}

export interface IsoPartnerRow {
  id: string;
  name?: string;
  email?: string;
  openCount: number;
  approvedCount: number;
  /** Computed from their own finished work — never a number somebody typed. */
  medianDaysToApproval: number | null;
  capabilities: { mccCodes?: string[]; states?: string[]; acceptingOrders?: boolean; note?: string };
  acceptingOrders: boolean;
}

export const merchantApi = {
  /** The queue. Unassigned first, oldest at the top — the server sorts it. */
  listApplications: (status?: string) =>
    req<{ applications: MerchantApplicationView[] }>(`/admin/applications${q({ status })}`),

  getApplication: (tenantId: string, applicationId: string, paymentUnitIds?: string[]) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}${q({
        tenantId, paymentUnitIds: paymentUnitIds?.join(',') })}`,
    ),

  /** Idempotent — the id is derived from the location. Safe to call on every open. */
  ensureApplication: (tenantId: string, locationId: string, locationName?: string) =>
    req<{ application: MerchantApplicationView; created: boolean }>('/admin/applications/ensure', {
      method: 'POST',
      body: JSON.stringify({ tenantId, locationId, locationName }),
    }),

  listPartners: () => req<{ partners: IsoPartnerRow[] }>('/admin/partners'),

  /**
   * WHO EARNED WHAT ON AN ORDER. Read-only, and the amounts are the ledger's
   * own — nothing here recomputes a commission. `wouldApplyPlan` is the
   * exception and is explicitly a RATE, never money: it says what a partner
   * would have earned had one been attributed, and the panel labels it so.
   */
  orderCommission: (orderId: string) =>
    req<{ commission: OrderCommissionView }>(
      `/admin/orders/${encodeURIComponent(orderId)}/commission`,
    ),

  assign: (tenantId: string, applicationId: string, isoAffiliateId: string, force?: { reason: string }) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/assign${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ isoAffiliateId, force: !!force, reason: force?.reason }) },
    ),

  unassign: (tenantId: string, applicationId: string, reason?: string) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/unassign${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  setStatus: (tenantId: string, applicationId: string, status: string, opts?: { reason?: string; force?: boolean }) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/status${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ status, ...opts }) },
    ),

  postMessage: (tenantId: string, applicationId: string, body: string, audience: MerchantMessageRole[]) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/messages${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ body, audience }) },
    ),

  requestDocument: (tenantId: string, applicationId: string, name: string, opts?: { comment?: string; sensitive?: boolean }) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/documents${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ name, ...opts }) },
    ),

  /**
   * BIND ONE SHEET TO ONE READER — the only place this is decided.
   *
   * Stamps `supersededAt` on whatever live sheet previously held that unit
   * server-side; nothing is ever deleted. The service 409s
   * ("One sheet, one reader") when `varId` is already bound to a DIFFERENT
   * unit, and 404s on an unknown `varId` — both surface via `req`'s own
   * error message, so callers show the service's words rather than a generic
   * failure.
   */
  bindVarSheet: (tenantId: string, applicationId: string, varId: string, unitId: string) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/var/${encodeURIComponent(varId)}/bind${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ unitId }) },
    ),

  /**
   * TAKE A SHEET BACK OFF ITS READER. Not a delete — the sheet stays on the
   * application and returns to the pool, so a statement bound by mistake can
   * come off and the right sheet go on. Any TID keyed off it is removed with
   * it, server-side: a terminal id whose sheet is unbound has no provenance.
   *
   * Same route as the bind. 409s when the sheet is not bound to anything.
   */
  unbindVarSheet: (tenantId: string, applicationId: string, varId: string) =>
    req<{ application: MerchantApplicationView }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/var/${encodeURIComponent(varId)}/bind${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ unbind: true }) },
    ),

  /**
   * A short-lived URL for ONE file.
   *
   * Never cached: the URL IS the permission, it expires in minutes, and every
   * read of a sensitive document is written to the access log before the URL
   * is minted — a record of who looked that is kept client-side is no record.
   */
  fileUrl: (tenantId: string, applicationId: string, kind: 'document' | 'quote' | 'var', id: string) =>
    req<{ url: string; expiresInSeconds: number }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/files${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ kind, id, mode: 'get' }) },
    ),

  /**
   * A presigned PUT for a file WE are putting in — the other half of
   * `fileUrl`. `id` is the record the file will hang off; for a new VAR sheet
   * that is a client-minted id passed straight back to `upsertVarSheet`, so
   * the key and the record agree without a round trip in between.
   */
  uploadUrl: (
    tenantId: string, applicationId: string,
    kind: 'document' | 'quote' | 'var',
    id: string, fileName: string, contentType: string,
  ) =>
    req<{ url: string; storageKey: string; expiresInSeconds: number }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/files${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify({ kind, id, mode: 'put', fileName, contentType }) },
    ),

  /**
   * THE SHEET, PUT IN BY US — for when the partner emails it instead of
   * uploading it. Omit `varId` to add one; pass it to edit that same sheet
   * (a corrected MID, rates typed in later, a re-issued file). Editing in
   * place is deliberate: appending a near-duplicate is how a record ends up
   * with three sheets and nobody knowing which programmed the reader.
   */
  upsertVarSheet: (
    tenantId: string, applicationId: string,
    body: {
      varId?: string; mid?: string; processor?: string; gateway?: string;
      mccCode?: string; fundingNote?: string; unitId?: string;
      storageKey?: string; fileName?: string;
      rates?: { credit?: MerchantRateLine; debit?: MerchantRateLine; note?: string };
    },
  ) =>
    req<{ application: MerchantApplicationView; varId: string }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/var${q({ tenantId })}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Only a sheet that is bound to nothing and keyed nothing — the wrong PDF. */
  deleteVarSheet: (tenantId: string, applicationId: string, varId: string) =>
    req<{ application: MerchantApplicationView; varId: string }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/var/${encodeURIComponent(varId)}${q({ tenantId })}`,
      { method: 'DELETE' },
    ),
};

/* ── display helpers, so three screens describe one record one way ──────── */

export const MERCHANT_STATUS_LABEL: Record<string, string> = {
  draft: 'Not started',
  existing_merchant: 'Brought their own',
  assigned: 'With partner',
  submitted: 'At the processor',
  info_requested: 'Waiting on merchant',
  approved: 'Approved',
  declined: 'Declined',
  abandoned: 'Not setting up',
};

/** The real UNIT_STATUS_CHIP palette — see hardwareApi.ts. Ruby family only. */
export const MERCHANT_STATUS_CHIP: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-700',
  existing_merchant: 'bg-gray-200 text-gray-700',
  assigned: 'bg-rose-100 text-rose-700',
  submitted: 'bg-rose-100 text-rose-700',
  info_requested: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  abandoned: 'bg-gray-200 text-gray-700',
};

/**
 * How many payment units are keyed, out of how many.
 *
 * COUNTS ASSIGNMENTS, not `status`. Repeating the server's rule here rather
 * than reading a flag keeps one screen from inventing a second definition of
 * "ready" — and a bring-your-own-processor store, which never reaches
 * `approved`, reads correctly on this screen for the same reason it ships.
 */
export function keyedCount(
  app: Pick<MerchantApplicationView, 'terminalAssignments'> | undefined,
  paymentUnitIds: readonly string[],
): { keyed: number; total: number; ready: boolean } {
  const total = paymentUnitIds.length;
  const keyed = paymentUnitIds.filter(
    (id) => (app?.terminalAssignments || []).some((t) => t.unitId === id),
  ).length;
  return { keyed, total, ready: total === 0 || keyed === total };
}
